import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Box,
  CardContent,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Alert,
  Divider,
  Chip,
  Avatar,
  Container,
  Paper,
  Grid,
  Stack,
  CircularProgress,
  Fade,
  Link,
} from "@mui/material";
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Error as ErrorIcon,
  LockOutlined as LockOutlinedIcon,
} from "@mui/icons-material";

const LoginPage = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { login, error: authError, clearError, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirected = useRef(false);

  // Redirect if already authenticated - only when isAuthenticated changes from false to true
  useEffect(() => {
    if (isAuthenticated && !hasRedirected.current) {
      hasRedirected.current = true;
      const from = location.state?.from?.pathname || "/dashboard";
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location.state?.from?.pathname]); // Include all dependencies

  // Reset redirect flag when component unmounts or isAuthenticated becomes false
  useEffect(() => {
    if (!isAuthenticated) {
      hasRedirected.current = false;
    }
  }, [isAuthenticated]);

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if form is valid for button state
  const isFormValid =
    formData.email.trim() !== "" &&
    formData.password.trim() !== "" &&
    formData.password.length >= 6 &&
    /\S+@\S+\.\S+/.test(formData.email);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    clearError();

    try {
      const result = await login(formData.email, formData.password);

      if (result.success) {
        const from = location.state?.from?.pathname || "/dashboard";
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Demo credentials from environment variables
  const demoCredentials = {
    admin: {
      email: import.meta.env.VITE_DEMO_ADMIN_EMAIL,
      password: import.meta.env.VITE_DEMO_ADMIN_PASSWORD,
    },
    agent: {
      email: import.meta.env.VITE_DEMO_AGENT_EMAIL,
      password: import.meta.env.VITE_DEMO_AGENT_PASSWORD,
    },
    manager: {
      email: import.meta.env.VITE_DEMO_MANAGER_EMAIL,
      password: import.meta.env.VITE_DEMO_MANAGER_PASSWORD,
    },
  };

  const demoButtons = [
    {
      role: "admin",
      label: "Admin",
      icon: <AdminIcon />,
      color: "primary",
      description: "Full access",
    },
    {
      role: "agent",
      label: "Agent",
      icon: <PersonIcon />,
      color: "secondary",
      description: "Customer support",
    },
    {
      role: "manager",
      label: "Manager",
      icon: <BusinessIcon />,
      color: "success",
      description: "Team management",
    },
  ];

  const handleDemoLogin = async (role) => {
    const creds = demoCredentials[role];

    if (!creds || !creds.email || !creds.password) {
      alert("Demo credentials not set in environment variables.");
      return;
    }
    setFormData(creds);
    setIsSubmitting(true);
    clearError();

    try {
      const result = await login(creds.email, creds.password);

      if (result.success) {
        const from = location.state?.from?.pathname || "/dashboard";
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error("Demo login error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // Reduced contrast gradient - more subtle
        background: "linear-gradient(135deg, #6b7ae8 0%, #7a5ba0 100%)",
        position: "relative",
        overflow: "hidden",
        py: { xs: 2, sm: 3 },
        px: 2,
      }}
    >
      {/* Subtle background pattern - reduced opacity */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          opacity: 0.3,
        }}
      />

      <Container maxWidth="sm">
        <Fade in timeout={600}>
          <Paper
            elevation={16}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              background: "rgba(255, 255, 255, 0.98)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(0, 0, 0, 0.05)",
            }}
          >
            {/* Header - Simplified */}
            <Box
              sx={{
                textAlign: "center",
                py: 3.5,
                px: 3,
                // Subtle header background - less dramatic
                background: "linear-gradient(135deg, #6b7ae8 0%, #7a5ba0 100%)",
                color: "white",
              }}
            >
              {/* Logo mark - Simple B icon instead of bolt */}
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  mx: "auto",
                  mb: 1.5,
                  background: "rgba(255, 255, 255, 0.15)",
                  fontSize: "1.75rem",
                  fontWeight: "bold",
                }}
              >
                B
              </Avatar>
              <Typography
                variant="h5"
                component="h1"
                gutterBottom
                fontWeight={600}
                sx={{ letterSpacing: "-0.02em" }}
              >
                Sign in to Bubakii
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                Access your logistics account
              </Typography>
            </Box>

            {/* Form */}
            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
              <form onSubmit={handleSubmit}>
                <Stack spacing={2.5}>
                  {/* Email Field - Lighter styling */}
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" sx={{ fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    placeholder="Enter your email"
                    autoComplete="email"
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "rgba(0, 0, 0, 0.02)",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "rgba(0, 0, 0, 0.02)",
                        },
                      },
                      "& .MuiInputLabel-root": {
                        fontWeight: 500,
                      },
                    }}
                  />

                  {/* Password Field - Lighter styling */}
                  <Box>
                    <TextField
                      fullWidth
                      label="Password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={handleChange}
                      error={!!errors.password}
                      helperText={errors.password}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="action" sx={{ fontSize: 20 }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              size="small"
                              sx={{
                                "&:hover": {
                                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                                },
                              }}
                            >
                              {showPassword ? (
                                <VisibilityOffIcon fontSize="small" />
                              ) : (
                                <VisibilityIcon fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "rgba(0, 0, 0, 0.02)",
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "rgba(0, 0, 0, 0.02)",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          fontWeight: 500,
                        },
                      }}
                    />
                    {/* Forgot password link */}
                    <Box sx={{ mt: 1, textAlign: "right" }}>
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={() => {
                          // TODO: Implement forgot password flow
                          console.log("Forgot password clicked");
                        }}
                        sx={{
                          color: "text.secondary",
                          textDecoration: "none",
                          fontSize: "0.875rem",
                          "&:hover": {
                            color: "primary.main",
                            textDecoration: "underline",
                          },
                        }}
                      >
                        Forgot password?
                      </Link>
                    </Box>
                  </Box>

                  {/* Auth Error */}
                  {authError && (
                    <Alert
                      severity="error"
                      icon={<ErrorIcon />}
                      sx={{ borderRadius: 1.5 }}
                    >
                      <Typography variant="body2" fontWeight={500}>
                        Authentication failed
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {authError}
                      </Typography>
                    </Alert>
                  )}

                  {/* Submit Button - More decisive */}
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isSubmitting || !isFormValid}
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <LockOutlinedIcon />
                      )
                    }
                    sx={{
                      py: 1.5,
                      mt: 1,
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #5568d3 0%, #6a4190 100%)",
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
                      },
                      "&:disabled": {
                        background: "rgba(0, 0, 0, 0.12)",
                        color: "rgba(0, 0, 0, 0.26)",
                      },
                      borderRadius: 2,
                      textTransform: "none",
                      fontSize: "1rem",
                      fontWeight: 600,
                      boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {isSubmitting ? "Signing in..." : "Sign In"}
                  </Button>

                  {/* Demo Accounts - Only show in development */}
                  {import.meta.env.DEV && (
                    <Box>
                      <Divider sx={{ my: 2.5 }}>
                        <Chip
                          label="Quick Demo Access"
                          variant="outlined"
                          size="small"
                          sx={{ background: "rgba(255, 255, 255, 0.8)" }}
                        />
                      </Divider>
                      <Grid container spacing={2}>
                        {demoButtons.map((button) => (
                          <Grid item xs={4} key={button.role}>
                            <Button
                              variant="outlined"
                              fullWidth
                              disabled={isSubmitting}
                              onClick={() => handleDemoLogin(button.role)}
                              startIcon={button.icon}
                              sx={{
                                py: 2,
                                flexDirection: "column",
                                gap: 1,
                                borderRadius: 2,
                                textTransform: "none",
                                borderColor: `${button.color}.main`,
                                color: `${button.color}.main`,
                                "&:hover": {
                                  borderColor: `${button.color}.dark`,
                                  backgroundColor: `${button.color}.50`,
                                },
                              }}
                            >
                              <Typography variant="body2" fontWeight="bold">
                                {button.label}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ opacity: 0.7 }}
                              >
                                {button.description}
                              </Typography>
                            </Button>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </Stack>
              </form>
            </CardContent>

            {/* Footer - More subtle */}
            <Box
              sx={{
                textAlign: "center",
                py: 2.5,
                px: 3,
                borderTop: "1px solid",
                borderColor: "divider",
                background: "rgba(0, 0, 0, 0.01)",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  opacity: 0.6,
                  fontSize: "0.8125rem",
                }}
              >
                © 2024 Bubakii Logistics. All rights reserved.
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{
                  opacity: 0.5,
                  fontSize: "0.75rem",
                  mt: 0.5,
                  display: "block",
                }}
              >
                Secure • Reliable • Professional
              </Typography>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default LoginPage;
